/**
 * faceVault.ts — Browser-native IndexedDB Target Face Storage.
 *
 * Stores user reference face photos and thumbnails locally inside the browser.
 * This guarantees user privacy, eliminates ongoing cloud storage costs,
 * and allows instant re-syncing to ephemeral cloud GPU worker nodes.
 */

export interface StoredFaceProfile {
  id: string;
  name: string;
  blob: Blob;
  dataUrl: string;
  createdAt: number;
  isActive: boolean;
}

const DB_NAME = "streampersona_vault";
const STORE_NAME = "faces";
const DB_VERSION = 1;

let dbPromise: Promise<IDBDatabase> | null = null;

function getDB(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;

  dbPromise = new Promise((resolve, reject) => {
    if (typeof indexedDB === "undefined") {
      reject(new Error("IndexedDB is not supported in this browser environment."));
      return;
    }

    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, { keyPath: "id" });
        store.createIndex("createdAt", "createdAt", { unique: false });
        store.createIndex("isActive", "isActive", { unique: false });
      }
    };

    request.onsuccess = () => {
      resolve(request.result);
    };

    request.onerror = () => {
      reject(request.error || new Error("Failed to open IndexedDB face vault."));
    };
  });

  return dbPromise;
}

/** Converts a Blob to a base64 DataURL for fast thumbnail rendering */
function blobToDataURL(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

/** Save a new target face profile into IndexedDB */
export async function saveFaceProfile(name: string, fileOrBlob: Blob): Promise<StoredFaceProfile> {
  const db = await getDB();
  const id = "face_" + Date.now() + "_" + Math.random().toString(36).substring(2, 9);
  const dataUrl = await blobToDataURL(fileOrBlob);

  const profile: StoredFaceProfile = {
    id,
    name: name.trim() || "Target Face",
    blob: fileOrBlob,
    dataUrl,
    createdAt: Date.now(),
    isActive: false,
  };

  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readwrite");
    const store = tx.objectStore(STORE_NAME);
    const req = store.add(profile);

    req.onsuccess = () => resolve(profile);
    req.onerror = () => reject(req.error || new Error("Failed to save face profile."));
  });
}

/** Retrieve all face profiles sorted by newest first */
export async function getAllFaceProfiles(): Promise<StoredFaceProfile[]> {
  const db = await getDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readonly");
    const store = tx.objectStore(STORE_NAME);
    const req = store.getAll();

    req.onsuccess = () => {
      const results = (req.result as StoredFaceProfile[]) || [];
      results.sort((a, b) => b.createdAt - a.createdAt);
      resolve(results);
    };
    req.onerror = () => reject(req.error || new Error("Failed to read face profiles."));
  });
}

/** Set a specific profile as active (and deactivate others) */
export async function setActiveFaceProfile(id: string): Promise<void> {
  const db = await getDB();
  const all = await getAllFaceProfiles();

  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readwrite");
    const store = tx.objectStore(STORE_NAME);

    all.forEach((p) => {
      const updated = { ...p, isActive: p.id === id };
      store.put(updated);
    });

    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error || new Error("Failed to set active face profile."));
  });
}

/** Retrieve the currently active face profile, if any */
export async function getActiveFaceProfile(): Promise<StoredFaceProfile | null> {
  const all = await getAllFaceProfiles();
  return all.find((p) => p.isActive) || (all.length > 0 ? all[0] : null);
}

/** Delete a face profile by ID */
export async function deleteFaceProfile(id: string): Promise<void> {
  const db = await getDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readwrite");
    const store = tx.objectStore(STORE_NAME);
    const req = store.delete(id);

    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error || new Error("Failed to delete face profile."));
  });
}

/** Clear all stored face profiles from the vault */
export async function clearFaceVault(): Promise<void> {
  const db = await getDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readwrite");
    const store = tx.objectStore(STORE_NAME);
    const req = store.clear();

    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error || new Error("Failed to clear face vault."));
  });
}
